import { createBrowserRouter, Navigate, Outlet } from 'react-router';
import { lazy, Suspense } from 'react';

// Layout components (current location - will move to src/components in Phase 7-8)
import { AppShell } from '@/components/layout/AppShell';
import { StoreHydration } from '@/components/layout/StoreHydration';
import { AuthGuard } from '@/components/layout/AuthGuard';
import { RoleRoute } from '@/components/layout/RoleRoute';

// Eager-load critical auth routes (current location - will move to src/pages in Phase 7-8)
import LoginPage from '@/app/login/page';
import LoginCallbackPage from '@/app/login/callback/page';

// Lazy-load all other routes for code splitting
const DashboardPage = lazy(() => import('@/app/dashboard/page'));
const AnalyticsPage = lazy(() => import('@/app/analytics/page'));
const ConsolePage = lazy(() => import('@/app/console/page'));
const ProjectionPage = lazy(() => import('@/app/projection/page'));
const BookingPage = lazy(() => import('@/app/booking/page'));
const HotelApprovalPage = lazy(() => import('@/app/hotel-approval/page'));
const HotelsPage = lazy(() => import('@/app/hotels/page'));
const HotelsRfpPage = lazy(() => import('@/app/hotels/rfp/page'));
const HotelsRfpDetailPage = lazy(() => import('@/app/hotels/rfp/[id]/page'));
const StationsPage = lazy(() => import('@/app/stations/page'));
const CrewPage = lazy(() => import('@/app/crew/page'));
const CrewMasterPage = lazy(() => import('@/app/crew-master/page'));
const RosterPage = lazy(() => import('@/app/roster/page'));
const ReportsPage = lazy(() => import('@/app/reports/page'));
const AccrualsPage = lazy(() => import('@/app/accruals/page'));
const InvoicesPage = lazy(() => import('@/app/invoices/page'));
const SettingsPage = lazy(() => import('@/app/settings/page'));
const AuditLogPage = lazy(() => import('@/app/audit-log/page'));
const OutboxPage = lazy(() => import('@/app/outbox/page'));
const EventsPage = lazy(() => import('@/app/events/page'));
const CommunicationPage = lazy(() => import('@/app/communication/page'));
const BroadcastPage = lazy(() => import('@/app/broadcast/page'));
const InventoryAlertsPage = lazy(() => import('@/app/inventory-alerts/page'));
const PeakDatesPage = lazy(() => import('@/app/peak-dates/page'));
const FeedbackPage = lazy(() => import('@/app/feedback/page'));
const TransportViewPage = lazy(() => import('@/app/transport-view/page'));
const NearestCrewPage = lazy(() => import('@/app/nearest-crew/page'));
const ApiInventoryPage = lazy(() => import('@/app/api-inventory/page'));
const RismaPage = lazy(() => import('@/app/risma/page'));
const QrDutyCodesPage = lazy(() => import('@/app/qr/duty-codes/page'));
const PrintInvoicePage = lazy(() => import('@/app/print/invoice/[id]/page'));

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-muted-foreground">Loading...</div>
    </div>
  );
}

// Wrapper component for lazy-loaded pages
function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

// Root layout with shell - uses Outlet for nested routes
function RootLayout() {
  return (
    <>
      <StoreHydration />
      <AppShell>
        <Outlet />
      </AppShell>
    </>
  );
}

export const router = createBrowserRouter([
  // Public routes (no auth required)
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/login/callback',
    element: <LoginCallbackPage />,
  },

  // Print routes (special layout - no shell, but needs auth)
  {
    path: '/print/invoice/:id',
    element: (
      <AuthGuard>
        <LazyPage>
          <PrintInvoicePage />
        </LazyPage>
      </AuthGuard>
    ),
  },

  // Protected routes with shell
  {
    path: '/',
    element: (
      <AuthGuard>
        <RootLayout />
      </AuthGuard>
    ),
    children: [
      // Root redirect
      { index: true, element: <Navigate to="/dashboard" replace /> },

      // Main routes
      {
        path: 'dashboard',
        element: (
          <LazyPage>
            <DashboardPage />
          </LazyPage>
        ),
      },
      {
        path: 'analytics',
        element: (
          <LazyPage>
            <AnalyticsPage />
          </LazyPage>
        ),
      },
      {
        path: 'console',
        element: (
          <LazyPage>
            <ConsolePage />
          </LazyPage>
        ),
      },
      {
        path: 'projection',
        element: (
          <LazyPage>
            <ProjectionPage />
          </LazyPage>
        ),
      },
      {
        path: 'booking',
        element: (
          <LazyPage>
            <BookingPage />
          </LazyPage>
        ),
      },
      {
        path: 'hotel-approval',
        element: (
          <LazyPage>
            <HotelApprovalPage />
          </LazyPage>
        ),
      },
      {
        path: 'hotels',
        element: (
          <LazyPage>
            <HotelsPage />
          </LazyPage>
        ),
      },
      {
        path: 'hotels/rfp',
        element: (
          <LazyPage>
            <HotelsRfpPage />
          </LazyPage>
        ),
      },
      {
        path: 'hotels/rfp/:id',
        element: (
          <LazyPage>
            <HotelsRfpDetailPage />
          </LazyPage>
        ),
      },
      {
        path: 'stations',
        element: (
          <LazyPage>
            <StationsPage />
          </LazyPage>
        ),
      },
      {
        path: 'crew',
        element: (
          <LazyPage>
            <CrewPage />
          </LazyPage>
        ),
      },
      {
        path: 'crew-master',
        element: (
          <LazyPage>
            <CrewMasterPage />
          </LazyPage>
        ),
      },
      {
        path: 'roster',
        element: (
          <LazyPage>
            <RosterPage />
          </LazyPage>
        ),
      },
      {
        path: 'reports',
        element: (
          <LazyPage>
            <ReportsPage />
          </LazyPage>
        ),
      },
      {
        path: 'accruals',
        element: (
          <LazyPage>
            <AccrualsPage />
          </LazyPage>
        ),
      },
      {
        path: 'invoices',
        element: (
          <LazyPage>
            <InvoicesPage />
          </LazyPage>
        ),
      },
      {
        path: 'settings',
        element: (
          <LazyPage>
            <SettingsPage />
          </LazyPage>
        ),
      },
      {
        path: 'audit-log',
        element: (
          <RoleRoute routeKey="audit-log">
            <LazyPage>
              <AuditLogPage />
            </LazyPage>
          </RoleRoute>
        ),
      },
      {
        path: 'outbox',
        element: (
          <RoleRoute routeKey="outbox">
            <LazyPage>
              <OutboxPage />
            </LazyPage>
          </RoleRoute>
        ),
      },
      {
        path: 'events',
        element: (
          <LazyPage>
            <EventsPage />
          </LazyPage>
        ),
      },
      {
        path: 'communication',
        element: (
          <LazyPage>
            <CommunicationPage />
          </LazyPage>
        ),
      },
      {
        path: 'broadcast',
        element: (
          <RoleRoute routeKey="broadcast">
            <LazyPage>
              <BroadcastPage />
            </LazyPage>
          </RoleRoute>
        ),
      },
      {
        path: 'inventory-alerts',
        element: (
          <LazyPage>
            <InventoryAlertsPage />
          </LazyPage>
        ),
      },
      {
        path: 'peak-dates',
        element: (
          <LazyPage>
            <PeakDatesPage />
          </LazyPage>
        ),
      },
      {
        path: 'feedback',
        element: (
          <LazyPage>
            <FeedbackPage />
          </LazyPage>
        ),
      },
      {
        path: 'transport-view',
        element: (
          <LazyPage>
            <TransportViewPage />
          </LazyPage>
        ),
      },
      {
        path: 'nearest-crew',
        element: (
          <LazyPage>
            <NearestCrewPage />
          </LazyPage>
        ),
      },
      {
        path: 'api-inventory',
        element: (
          <LazyPage>
            <ApiInventoryPage />
          </LazyPage>
        ),
      },
      {
        path: 'risma',
        element: (
          <LazyPage>
            <RismaPage />
          </LazyPage>
        ),
      },
      {
        path: 'qr/duty-codes',
        element: (
          <LazyPage>
            <QrDutyCodesPage />
          </LazyPage>
        ),
      },
    ],
  },
]);
